import Wordmark from '@/components/layout/Wordmark'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

export default function PrivacyPage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d0a1f] to-[#12103a] text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/50 hover:text-white/80 transition mb-8 py-2 -ml-2 pl-2 pr-3 rounded-lg"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <Wordmark size="lg" variant="light" className="justify-center mb-4" />

        <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2">Privacy Policy</h1>
        <p className="text-white/50 text-center mb-12">
          Effective Date: May 1, 2026
        </p>

        <div className="space-y-10 text-white/80 leading-relaxed">
          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">1.</span>Introduction
            </h2>
            <p>
              LegalLady AI ("we," "us," "our"), a California company, operates the legallady.ai and
              app.legallady.ai websites, our iOS and Android mobile applications, and any future
              applications or services (collectively, the "Service"). This Privacy Policy explains
              how we collect, use, disclose, and protect information when you use our Service across
              any platform, whether you are a law firm subscriber, a team member, or a consumer
              interacting with an AI chat or voice agent deployed by a law firm through our platform.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">2.</span>Information We Collect
            </h2>

            <h3 className="text-white font-medium mt-4 mb-1">Account and Organization Information</h3>
            <p>
              Name, email address, phone number, organization name, role, and billing information
              provided during registration and account setup.
            </p>

            <h3 className="text-white font-medium mt-4 mb-1">Chat Widget Data</h3>
            <p>
              When website visitors interact with an AI chat widget deployed by a law firm, we
              collect the conversation content, visitor name and contact information (if provided),
              IP address, browser type, and referring URL.
            </p>

            <h3 className="text-white font-medium mt-4 mb-1">Voice and Call Data</h3>
            <p>
              For AI voice agent interactions, we collect call recordings, call duration, phone
              numbers, transcripts, and metadata associated with inbound and outbound calls.
            </p>

            <h3 className="text-white font-medium mt-4 mb-1">Usage and Analytics Data</h3>
            <p>
              We collect information about how you use the platform, including pages visited,
              features used, actions taken, device information, and performance metrics.
            </p>

            <h3 className="text-white font-medium mt-4 mb-1">Lead and CRM Data</h3>
            <p>
              Information captured through intake workflows, including lead details, booking
              information, case type, and notes added by law firm staff.
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">3.</span>How We Use Your Information
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide, operate, and maintain the Service</li>
              <li>Process and manage AI chat and voice interactions</li>
              <li>Improve our AI models and service quality</li>
              <li>Generate analytics and reports for law firm subscribers</li>
              <li>Process billing and subscription management</li>
              <li>Send service-related notifications and updates</li>
              <li>Provide customer support</li>
              <li>Detect and prevent fraud, abuse, or security incidents</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">4.</span>Third-Party Service Providers
            </h2>
            <p>
              We use trusted third-party service providers to operate our platform. These providers
              only receive the data necessary to perform their specific function:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong className="text-white">Telephony &amp; Voice Providers</strong> — AI voice agents, telephony, SMS, and phone number provisioning</li>
              <li><strong className="text-white">Cloud Infrastructure Providers</strong> — Application hosting, database storage, and content delivery</li>
              <li><strong className="text-white">Payment Processors</strong> — Secure payment and subscription management</li>
            </ul>
            <p className="mt-2">
              Each provider is bound by their own privacy policies and data processing agreements.
              A list of current sub-processors is available upon request.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">5.</span>Data Sharing
            </h2>
            <p>
              We do not sell your personal information. We share data only in the following
              circumstances:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>With third-party service providers as described above, solely to operate the Service</li>
              <li>With the law firm whose AI agent you interacted with (for consumer visitors)</li>
              <li>When required by law, subpoena, or legal process</li>
              <li>To protect the rights, safety, or property of LegalLady AI, our users, or the public</li>
              <li>In connection with a merger, acquisition, or sale of assets (with prior notice)</li>
            </ul>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">6.</span>Call Recording Disclosure
            </h2>
            <p>
              Calls handled by AI voice agents may be recorded. Recordings are stored securely and
              are accessible to the law firm subscriber who deployed the agent. Law firms are
              responsible for ensuring compliance with applicable call recording consent laws,
              including California's two-party consent requirement (Cal. Penal Code Section 632).
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">7.</span>Chat Data
            </h2>
            <p>
              Conversations between website visitors and AI chat agents are stored and made
              available to the law firm subscriber through the LegalLady AI dashboard. Chat
              transcripts may contain personal information voluntarily provided by the visitor.
              This data is treated as Customer Data owned by the law firm subscriber.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">8.</span>Data Retention
            </h2>
            <p>
              We retain your data for as long as your account is active or as needed to provide the
              Service. After account termination, we retain Customer Data for 30 days to allow for
              export, after which it may be permanently deleted. We may retain certain data longer
              as required by law or for legitimate business purposes such as resolving disputes.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">9.</span>Outbound Communication Data
            </h2>
            <p>
              When our platform places outbound calls on behalf of a law firm subscriber, we collect
              and store the phone number called, call duration, call recording (if enabled), transcript,
              and outcome. Outbound calls are only placed to individuals who have provided prior express
              consent through a lead intake form or equivalent opt-in mechanism.
            </p>
            <p className="mt-2">
              Recipients of outbound calls may request to be added to a do-not-call list by informing
              the AI agent during the call or by contacting the law firm directly. Law firms are
              responsible for maintaining their own do-not-call lists and honoring opt-out requests.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">10.</span>Your Rights Under the CCPA
            </h2>
            <p>
              If you are a California resident, you have the following rights under the California
              Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong className="text-white">Right to Know:</strong> Request what personal information we have collected about you</li>
              <li><strong className="text-white">Right to Delete:</strong> Request deletion of your personal information</li>
              <li><strong className="text-white">Right to Opt-Out:</strong> Opt out of the sale of personal information (we do not sell personal information)</li>
              <li><strong className="text-white">Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights</li>
            </ul>
            <p className="mt-2">
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:support@legallady.ai" className="text-indigo-400 hover:text-indigo-300 underline">
                support@legallady.ai
              </a>
              . We will respond within 45 days.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">11.</span>International Users and GDPR
            </h2>
            <p>
              If you are located in the European Economic Area (EEA), United Kingdom, or other
              jurisdiction with data protection laws, you may have additional rights regarding your
              personal data, including:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
            </ul>
            <p className="mt-2">
              LegalLady AI processes data based on the following legal bases: (a) performance of a
              contract (providing the Service); (b) legitimate interests (improving and securing the
              Service); and (c) consent (where applicable).
            </p>
            <p className="mt-2">
              To exercise any GDPR rights, contact us at{' '}
              <a href="mailto:support@legallady.ai" className="text-indigo-400 hover:text-indigo-300 underline">
                support@legallady.ai
              </a>
              . We will respond within 30 days.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">12.</span>Data Processing Agreement
            </h2>
            <p>
              Law firms using LegalLady AI to process client or prospective client data may require a
              Data Processing Agreement (DPA) to comply with their ethical obligations and applicable
              data protection laws. We make a standard DPA available upon request.
            </p>
            <p className="mt-2">
              To request a DPA, contact us at{' '}
              <a href="mailto:support@legallady.ai" className="text-indigo-400 hover:text-indigo-300 underline">
                support@legallady.ai
              </a>
              .
            </p>
            <p className="mt-2">
              Law firms are responsible for ensuring their use of LegalLady AI complies with applicable
              legal ethics rules, including rules governing client confidentiality, data security, and
              the use of technology in legal practice.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">13.</span>Security
            </h2>
            <p>
              We implement industry-standard security measures to protect your data, including
              encrypted data transmission (TLS), secure database storage, JWT-based authentication,
              role-based access controls, and regular security reviews. However, no method of
              electronic transmission or storage is 100% secure, and we cannot guarantee absolute
              security.
            </p>
          </section>

          {/* 14 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">14.</span>AI Interaction Disclosure
            </h2>
            <p>
              When consumers interact with AI chat or voice agents deployed through the LegalLady AI
              platform, the AI agent's greeting is configured to identify itself as an AI assistant.
              Law firms are required under our Terms of Service to maintain this disclosure and ensure
              consumers are aware they are communicating with an artificial intelligence system, not a
              human attorney or staff member.
            </p>
            <p className="mt-2">
              Information shared during AI interactions is stored securely and made available to the
              subscribing law firm. Consumers should not share sensitive personal information (such as
              Social Security numbers, financial account numbers, or privileged legal communications)
              through AI chat or voice agents.
            </p>
          </section>

          {/* 15 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">15.</span>Children's Privacy
            </h2>
            <p>
              The Service is not directed to individuals under the age of 13. We do not knowingly
              collect personal information from children under 13. If we become aware that we have
              collected such information, we will take steps to delete it promptly. If you believe a
              child has provided us with personal information, please contact us at{' '}
              <a href="mailto:support@legallady.ai" className="text-indigo-400 hover:text-indigo-300 underline">
                support@legallady.ai
              </a>
              .
            </p>
          </section>

          {/* 16 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">16.</span>Cookies and Tracking
            </h2>
            <p>
              We use cookies and similar technologies to maintain your session, remember preferences,
              and collect usage analytics. The chat widget deployed on law firm websites may also use
              cookies to maintain conversation state. You can control cookie settings through your
              browser, but disabling cookies may affect functionality.
            </p>
          </section>

          {/* 17 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">17.</span>Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. When we make material changes, we
              will notify you via email or through the platform and update the effective date at the
              top of this page. We encourage you to review this policy periodically.
            </p>
          </section>

          {/* 18 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">18.</span>Contact Us
            </h2>
            <p>
              If you have questions about this Privacy Policy or wish to exercise your privacy
              rights, contact us at:
            </p>
            <div className="mt-2">
              <p>LegalLady AI</p>
              <p>
                Email:{' '}
                <a href="mailto:support@legallady.ai" className="text-indigo-400 hover:text-indigo-300 underline">
                  support@legallady.ai
                </a>
              </p>
              <p>
                Website:{' '}
                <a href="https://legallady.ai" className="text-indigo-400 hover:text-indigo-300 underline">
                  legallady.ai
                </a>
              </p>
            </div>
          </section>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 text-center text-white/40 text-sm">
          &copy; {new Date().getFullYear()} LegalLady AI. All rights reserved.
        </div>
      </div>
    </div>
  )
}
